import { useState, useCallback } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useMessageManager } from '@framework/message';
import DictionaryRepository from '@/creditflowresearch/infrastructure/repository/DictionaryRepository';
import { RpcError } from '@dealroadshow/json-rpc-dispatcher';
import Logger from '@framework/sentry/Logger';
import type { IMarketTheme } from '@/creditflowresearch/domain/vo/ArticlesAndStats/IMarketTheme';
import { ITheme } from '@/condor/domain/vo/Finsight/CreditFlowResearch/ITheme';
import { IMarketThemeGroup } from '@/creditflowresearch/domain/vo/Dictionary/MarketThemes';

export const useThemes = (themes?: IMarketTheme[] | ITheme[] | IMarketThemeGroup[], isGrouped = false) => {
  const [collection, setCollection] = useState<IMarketTheme[] | ITheme[] | IMarketThemeGroup[]>(themes || []);
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const container = getContainer();
  const { getErrorMessage } = useMessageManager();

  const fetch = useCallback(async () => {
    setIsFetching(true);
    try {
      const dictionaryRepository = container.get<DictionaryRepository>(DictionaryRepository);
      const response = await dictionaryRepository.getThemes({ isGrouped });
      setCollection(response);
      setIsInitialized(true);
    } catch (error) {
      if (error instanceof RpcError) {
        AlertManager.error(getErrorMessage(error));
      } else {
        Logger.error({ message: 'useThemes failed', error });
        AlertManager.error('An unexpected error occurred while loading themes. Please try again.');
      }
    } finally {
      setIsFetching(false);
    }
  }, [container, isGrouped, getErrorMessage]);

  return {
    isFetching,
    isInitialized,
    collection,
    fetch,
  };
};
